import moment from 'moment'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { getUser } from '../../../redux/store/user/getters'
import { canSeeMyContracts } from '../../../helper/common'
import { NOTIFICATION_TYPE_TO_ROUTE } from '../constants'
import { withExtraQuery } from '../utils'
import { TOOLTIP } from '../../../constants'
import { ReactComponent as UnreadIndicator } from 'assets/svg/UnreadIndicator16.svg'
import { ReactComponent as ReadIndicator } from 'assets/svg/ReadIndicator16.svg'
import { ReactComponent as SharedIcon } from 'assets/svg/Shared16.svg'
import { NotificationCardProps } from './types'
import { NotificationActions } from '../types'
import './styles.scss'
import cn from 'classnames'

export const NotificationCard = ({
  data,
  onMarkAsRead
}: NotificationCardProps) => {
  const { uuid, is_read, payload, description, created_at } = data

  // flag from BE if notification is shared across different Sourcing Leaders
  const isNotShared = !payload.extra?.shared

  const onClick = useCallback(
    () => onMarkAsRead(uuid, { is_read: !is_read }),
    [uuid, is_read, onMarkAsRead]
  )

  const user = useSelector(getUser)

  const redirectTab = withExtraQuery(
    NOTIFICATION_TYPE_TO_ROUTE(payload.object_id, canSeeMyContracts(user.role))[
      payload.content_type
    ] || '',
    payload.extra
  )

  return (
    <div className="notification-card column overflowX-hidden border-bottom">
      <div className="notification-card__content flex space-between align-start">
        <div className="notification-card__message">
          {payload.action === NotificationActions.OPEN ? (
            <NavLink target="_blank" to={redirectTab}>
              <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                {description}
              </Typography.Body1>
            </NavLink>
          ) : (
            <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
              {description}
            </Typography.Body1>
          )}
        </div>
        <div className="notification-card__info flex space-between align-center">
          <Tooltip
            title={TOOLTIP.SHARED_NOTIFICATION}
            overlayInnerStyle={{
              display: isNotShared ? 'none' : 'block'
            }}
            placement="top"
            className="notification-card__info--tooltip"
            getPopupContainer={(node) => document?.body ?? node}
          >
            <div
              className={cn('notification-card__info--tooltip--icon', {
                hidden: isNotShared
              })}
            >
              <SharedIcon />
            </div>
          </Tooltip>
          <Typography.Label
            className="notification-card__created-at"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
            uppercase
          >
            {moment(created_at).format('hh:mm a')}
          </Typography.Label>
          <Tooltip
            title={is_read ? TOOLTIP.MARK_AS_UNREAD : TOOLTIP.MARK_AS_READ}
            placement="topLeft"
            getPopupContainer={(node) => document?.body ?? node}
          >
            <Button.Icon
              type={BUTTON_TYPES.GHOST}
              icon={!is_read ? <UnreadIndicator /> : <ReadIndicator />}
              onClick={onClick}
              small
            />
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
